import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { Field, FormikProps } from "formik";
import { AllocationTag } from "../types";
import { commonButtonProps, commonTextFieldProps } from "../../../utils/styles";
import { SearchableEntity } from "../services/searchServiceProject";
import { formatText } from "../../../utils/utils";
import ProjectSearchableAutocomplete from "./tabs/ProjectSearchableAutoComplete";
import { GlobalLabels } from "../enums";
import { getMetaTypes } from "../enums/metaTags";

interface AllocationTagsSectionProps {
  allocationIndex: number;
  allocation: any;
  formik: FormikProps<any>;
  isSubmitting: boolean;
  projectId: string;
  editAllocation?: boolean;
}

const AllocationTagsSection: React.FC<AllocationTagsSectionProps> = ({
  allocationIndex,
  allocation,
  formik,
  isSubmitting,
  projectId,
  editAllocation,
}) => {
  const [selectedItems, setSelectedItems] = useState<Record<number, any>>({});
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;
  const hasVehicleAllocation = allocation?.allocationTags?.some(
    (tag: AllocationTag) => tag.category2Type === "VEHICLE"
  );

  // Group schedule tags by route for edit mode
  const groupedScheduleTags = useMemo(() => {
    if (!editAllocation) return null;

    const scheduleTags =
      allocation?.allocationTags?.filter(
        (tag: AllocationTag) => tag.category2Type === "SCHEDULE"
      ) || [];

    const grouped: Record<
      string,
      { route: any; schedules: any[]; selectedScheduleIds: string[] }
    > = {};

    scheduleTags.forEach((tag: AllocationTag) => {
      if (tag.category2Info?.route?.id) {
        const routeId = tag.category2Info.route.id;
        if (!grouped[routeId]) {
          grouped[routeId] = {
            route: tag.category2Info.route,
            schedules: tag.category2Info.route.schedules || [],
            selectedScheduleIds: [],
          };
        }
        // Add the schedule ID to selected schedules
        if (tag.category2Id) {
          grouped[routeId].selectedScheduleIds.push(tag.category2Id);
        }
      }
    });

    return grouped;
  }, [allocation?.allocationTags, editAllocation]);

  const addAllocationTag = (type?: string) => {
    const newAllocationTag: AllocationTag = {
      category1Type: allocation.category,
      category1Id: allocation.categoryId,
      category2Type: type || "",
      category2Id: "",
      startDate: new Date(allocation.startDate).toISOString().split("T")[0],
      createdBy: localUser ? JSON.parse(localUser).id : "",
      companyId: companyId,
    };
    const currentAllocationTags =
      formik.values.allocations[allocationIndex].allocationTags;
    formik.setFieldValue(`allocations.${allocationIndex}.allocationTags`, [
      ...currentAllocationTags,
      newAllocationTag,
    ]);
  };

  const removeAllocationTag = (tagIndex: number) => {
    const currentAllocationTags =
      formik.values.allocations[allocationIndex].allocationTags;
    const newAllocationTags = currentAllocationTags.filter(
      (_: any, index: number) => index !== tagIndex
    );
    formik.setFieldValue(
      `allocations.${allocationIndex}.allocationTags`,
      newAllocationTags
    );
    setSelectedItems((prev) => {
      const updated = { ...prev };
      delete updated[tagIndex];
      return updated;
    });
  };

  const handleValueSelect = (
    tagIndex: number,
    selectedItem: any,
    metaType: string
  ) => {
    if (selectedItem) {
      setSelectedItems((prev) => ({ ...prev, [tagIndex]: selectedItem }));
      formik.setFieldValue(
        `allocations.${allocationIndex}.allocationTags.${tagIndex}.category1Id`,
        allocation.categoryId
      );
      formik.setFieldValue(
        `allocations.${allocationIndex}.allocationTags.${tagIndex}.category2Id`,
        selectedItem.id
      );
      formik.setFieldValue(
        `allocations.${allocationIndex}.allocationTags.${tagIndex}.category2Type`,
        metaType
      );
    }
  };

  const getOptionLabel = (option: any, metaType: string) => {
    if (typeof option === "string") return option;
    switch (metaType) {
      case "VEHICLE":
        return option.vehicleNumber;
      case "DRIVER":
      case "CLEANER":
      case "LADY":
      case "MANAGER":
        return `${option.name} - ${
          option.phoneNumber || option.contactNumber || "N/A"
        }`;
      case "ROUTE":
        return `${option.fromLocation} - ${option.toLocation}`;
      default:
        return option.name || option.id || "";
    }
  };

  // For display only: you may want to fetch/display the label for the selected id
  const getDisplayValue = (
    tag: AllocationTag,
    metaType: string,
    tagIndex: number
  ) => {
    const selectedItem = selectedItems[tagIndex];
    if (selectedItem && tag.category2Id === selectedItem.id) {
      return getOptionLabel(selectedItem, metaType);
    }
    if (tag.category2Info) {
      return getOptionLabel(tag.category2Info, metaType);
    }
    return tag.category2Id || "";
  };

  const renderValueField = (
    tagIndex: number,
    metaType: string,
    tag: AllocationTag
  ) => {
    const searchableEntity = metaType as SearchableEntity;
    if (searchableEntity) {
      return (
        <Box>
          <ProjectSearchableAutocomplete
            entityType={searchableEntity}
            value={getDisplayValue(tag, metaType, tagIndex)}
            onSelect={(option) => handleValueSelect(tagIndex, option, metaType)}
            onChange={() => {}}
            label={`Select ${metaType.toLowerCase()}`}
            placeholder={`Search ${metaType.toLowerCase()}...`}
            getOptionLabel={(option) => getOptionLabel(option, metaType)}
            renderOptionContent={(option) =>
              metaType === "ROUTE"
                ? `${option.fromLocation} - ${option.toLocation}`
                : getOptionLabel(option, metaType)
            }
            disabled={isSubmitting || (editAllocation && Boolean(tag?.id))}
            size="small"
            projectId={projectId}
          />
        </Box>
      );
    } else {
      return (
        <Field
          as={TextField}
          name={`allocations.${allocationIndex}.allocationTags.${tagIndex}.category2Id`}
          label="Value"
          fullWidth
          placeholder="Enter value"
          disabled={isSubmitting}
          {...commonTextFieldProps}
        />
      );
    }
  };

  const handleScheduleChange = (e: any, schedule: any, tagIndex: number) => {
    const isSelected = e.target.checked;
    if (isSelected) {
      formik.setFieldValue(
        `allocations.${allocationIndex}.allocationTags.${tagIndex}.schedules`,
        [
          ...(formik.values.allocations[allocationIndex].allocationTags[
            tagIndex
          ].schedules || []),
          schedule,
        ]
      );
    } else {
      formik.setFieldValue(
        `allocations.${allocationIndex}.allocationTags.${tagIndex}.schedules`,
        formik.values.allocations[allocationIndex].allocationTags[
          tagIndex
        ].schedules?.filter((s: any) => s?.schedule !== schedule.schedule)
      );
    }
  };

  // Handle schedule selection for grouped schedule tags
  const handleGroupedScheduleChange = (
    e: any,
    schedule: any,
    routeId: string
  ) => {
    const isSelected = e.target.checked;
    const currentAllocationTags =
      formik.values.allocations[allocationIndex].allocationTags;

    if (isSelected) {
      // Add new schedule tag
      const newScheduleTag: AllocationTag = {
        category1Type: allocation.category,
        category1Id: allocation.categoryId,
        category2Type: "SCHEDULE",
        category2Id: schedule.id,
        startDate: new Date().toISOString().split("T")[0],
        createdBy: localUser ? JSON.parse(localUser).id : "",
        companyId: companyId,
        category2Info: {
          ...schedule,
          route: groupedScheduleTags?.[routeId]?.route,
        },
      };

      formik.setFieldValue(`allocations.${allocationIndex}.allocationTags`, [
        ...currentAllocationTags,
        newScheduleTag,
      ]);
    } else {
      // Remove schedule tag
      const updatedTags = currentAllocationTags.filter(
        (tag: AllocationTag) =>
          !(tag.category2Type === "SCHEDULE" && tag.category2Id === schedule.id)
      );

      formik.setFieldValue(
        `allocations.${allocationIndex}.allocationTags`,
        updatedTags
      );
    }
  };

  // Remove all schedule tags for a specific route
  const removeAllScheduleTagsForRoute = (routeId: string) => {
    const currentAllocationTags =
      formik.values.allocations[allocationIndex].allocationTags;

    const updatedTags = currentAllocationTags.filter((tag: AllocationTag) => {
      if (tag.category2Type === "SCHEDULE") {
        // Keep only if it's not a schedule tag for this route
        return !(tag.category2Info?.route?.id === routeId);
      }
      return true;
    });

    formik.setFieldValue(
      `allocations.${allocationIndex}.allocationTags`,
      updatedTags
    );
  };

  // Check if a schedule is selected in the current allocation tags
  const isScheduleSelected = (scheduleId: string) => {
    const currentAllocationTags =
      formik.values.allocations[allocationIndex].allocationTags;
    return currentAllocationTags.some(
      (tag: AllocationTag) =>
        tag.category2Type === "SCHEDULE" && tag.category2Id === scheduleId
    );
  };

  return (
    <Grid item xs={12}>
      {!editAllocation && <Divider sx={{ my: 1.5 }} />}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 500, fontSize: "0.8rem", mr: 1 }}
        >
          {GlobalLabels.PROJECT.TAG.PLURAL}
        </Typography>
        <Box>
          {getMetaTypes(allocation.category)?.map((type) => {
            if (editAllocation && type === "VEHICLE" && hasVehicleAllocation) {
              return null;
            }
            return (
              <Button
                onClick={() => addAllocationTag(type)}
                startIcon={<AddIcon sx={{ fontSize: "0.7rem" }} />}
                variant="outlined"
                disabled={!allocation.category || isSubmitting}
                {...commonButtonProps}
                sx={{
                  px: 1,
                  py: 0.25,
                  mr: 1,
                  fontSize: "0.7rem",
                  border: "1px dashed",
                }}
              >
                {formatText(type)}
              </Button>
            );
          })}
        </Box>
      </Box>
      <Box>
        {/* Render grouped schedule tags for edit mode */}
        {editAllocation &&
          groupedScheduleTags &&
          Object.keys(groupedScheduleTags).length > 0 && (
            <>
              {Object.entries(groupedScheduleTags).map(
                ([routeId, { route, schedules, selectedScheduleIds }]) => (
                  <Grid container spacing={1.5} key={routeId} mb={1}>
                    <Grid item xs={12} md={1} sx={{ display: "flex" }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontSize: "0.75rem", mt: 1 }}
                      >
                        Route :
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Route"
                        value={`${route.fromLocation} - ${route.toLocation}`}
                        fullWidth
                        disabled
                        {...commonTextFieldProps}
                      />
                    </Grid>
                    <Grid container item xs={12} md={6.5}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontSize: "0.75rem",
                          minWidth: "100px",
                          mt: 1,
                        }}
                      >
                        Select Schedules
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          flex: 1,
                        }}
                      >
                        {schedules.map((schedule: any) => {
                          const isSelected = isScheduleSelected(schedule.id);
                          return (
                            <FormControlLabel
                              key={schedule.id}
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  size="small"
                                  sx={{
                                    "& .MuiSvgIcon-root": {
                                      fontSize: "1rem",
                                    },
                                    "& .MuiFormControlLabel-label": {
                                      fontSize: "0.75rem",
                                    },
                                  }}
                                />
                              }
                              label={`${formatText(schedule.schedule)} - ${
                                schedule.from
                              }`}
                              sx={{
                                fontSize: "0.7rem",
                                "& .MuiFormControlLabel-label": {
                                  fontSize: "0.75rem",
                                },
                              }}
                              onChange={(e: any) => {
                                handleGroupedScheduleChange(
                                  e,
                                  schedule,
                                  routeId
                                );
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={0.5}>
                      <IconButton
                        onClick={() => removeAllScheduleTagsForRoute(routeId)}
                        size="small"
                        color="error"
                        disabled={isSubmitting}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                )
              )}
            </>
          )}

        {/* Render non-schedule tags and schedule tags in non-edit mode */}
        {allocation?.allocationTags?.map(
          (tag: AllocationTag, tagIndex: number) => {
            // Skip schedule tags in edit mode as they're handled above
            if (editAllocation && tag.category2Type === "SCHEDULE") {
              return null;
            }

            return (
              <Grid
                container
                spacing={1.5}
                key={tagIndex}
                mb={1}
                alignItems="center"
              >
                <Grid item xs={12} md={1} sx={{ display: "flex" }}>
                  <Typography variant="subtitle2" sx={{ fontSize: "0.75rem" }}>
                    {formatText(tag.category2Type)} :
                  </Typography>
                </Grid>
                <Grid item xs={11} md={4}>
                  {tag.category2Type ? (
                    renderValueField(tagIndex, tag.category2Type, tag)
                  ) : (
                    <TextField
                      label="Select type first"
                      fullWidth
                      disabled
                      {...commonTextFieldProps}
                    />
                  )}
                </Grid>
                {tag.category2Type === "ROUTE" &&
                  (() => {
                    // Simple approach: use tag.category2Info directly in edit mode
                    const selectedItem =
                      editAllocation && tag?.id
                        ? tag.category2Info
                        : selectedItems[tagIndex];

                    if (editAllocation && tag?.id) {
                      // In edit mode, we don't need to show the schedules as user while creating didn't select any schedule
                      return null;
                    }

                    if (
                      selectedItem &&
                      selectedItem.schedules &&
                      selectedItem.schedules.length > 0
                    ) {
                      return (
                        <Grid container item xs={12} md={6.5}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontSize: "0.75rem",
                              minWidth: "100px",
                              mt: 1,
                            }}
                          >
                            Select Schedules
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              flex: 1,
                            }}
                          >
                            {selectedItem.schedules.map(
                              (schedule: any, index: number) => {
                                // Check if this schedule is already selected in the form
                                const existingSchedules =
                                  formik.values.allocations[allocationIndex]
                                    .allocationTags[tagIndex].schedules || [];
                                const isSelected = existingSchedules.some(
                                  (s: any) => s?.schedule === schedule.schedule
                                );

                                return (
                                  <FormControlLabel
                                    key={index}
                                    control={
                                      <Checkbox
                                        checked={isSelected}
                                        size="small"
                                        sx={{
                                          "& .MuiSvgIcon-root": {
                                            fontSize: "1rem",
                                          },
                                          "& .MuiFormControlLabel-label": {
                                            fontSize: "0.75rem",
                                          },
                                        }}
                                      />
                                    }
                                    label={`${formatText(
                                      schedule.schedule
                                    )} - ${schedule.from}`}
                                    sx={{
                                      fontSize: "0.7rem",
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: "0.75rem",
                                      },
                                    }}
                                    onChange={(e: any) => {
                                      handleScheduleChange(
                                        e,
                                        schedule,
                                        tagIndex
                                      );
                                    }}
                                  />
                                );
                              }
                            )}
                          </Box>
                        </Grid>
                      );
                    }
                    return null;
                  })()}
                <Grid item xs={0.5} md={0.5}>
                  <IconButton
                    onClick={() => removeAllocationTag(tagIndex)}
                    size="small"
                    color="error"
                    disabled={isSubmitting}
                  >
                    <DeleteIcon style={{ fontSize: "1rem" }} />
                  </IconButton>
                </Grid>
              </Grid>
            );
          }
        )}
        {allocation?.allocationTags?.length === 0 && !editAllocation && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              textAlign: "center",
              py: 1.5,
              fontSize: "0.75rem",
            }}
          >
            No additional tags added
          </Typography>
        )}
      </Box>
    </Grid>
  );
};

export default AllocationTagsSection;
